import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';

// Pie chart library (Chart.js or similar)
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const GeneralDashboard = ({ setContentOpen }) => {
    const [profile, setProfile] = useState({ firstName: '', lastName: '', roleName: '' });
    const navigate = useNavigate();
    const [showFormsModal, setShowFormsModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [formsContent, setFormsContent] = useState(<div className="text-center text-muted">Loading forms...</div>);
    const [settings, setSettings] = useState({ firstName: '', lastName: '', email: '' });
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [dashboardStats, setDashboardStats] = useState({ volunteersRegistered: 0, patientsServed: 0, clinicsHosted: 0 });
    const [upcomingDeployments, setUpcomingDeployments] = useState([]);
    const [volunteerTypes, setVolunteerTypes] = useState({ labels: [], data: [] });

    // Replace with your API base URL
    const baseAPIurl = window.baseAPIurl || process.env.REACT_APP_BACKURL || '';

    useEffect(() => {
        // Fetch user profile
        fetch(baseAPIurl + '/api/foodPantry/userProfile', { credentials: 'include' })
            .then(res => res.ok ? res.json() : Promise.reject())
            .then(data => {
                setProfile({
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    roleName: data.roleName || '',
                });
                setSettings({
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    email: data.email || '',
                });
            })
            .catch(() => setProfile({ firstName: '', lastName: '', roleName: '' }));

        // Example: fetch dashboard stats, deployments, volunteer types
        // Replace with real API endpoints
        setDashboardStats({ volunteersRegistered: 42, patientsServed: 123, clinicsHosted: 7 });
        setUpcomingDeployments(["No upcoming deployments."]);
        setVolunteerTypes({ labels: ["Medical", "General", "Chaplain"], data: [10, 25, 7] });
    }, []);

    // Forms modal content loader
    const loadFormsContent = () => {
        setFormsContent(<div className="text-center text-muted">Loading forms...</div>);
        fetch(baseAPIurl + '/api/foodPantry/myWaivers', { credentials: 'include' })
            .then(res => res.ok ? res.json() : Promise.reject())
            .then(data => {
                // Build accordion content (simplified)
                let html = [];
                html.push(
                    <div className="accordion mb-4" id="waiverAccordion" key="waiverAccordion">
                        <div className="accordion-item">
                            <h2 className="accordion-header" id="hipaaHeading">
                                <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#hipaaCollapse" aria-expanded="true" aria-controls="hipaaCollapse">
                                    HIPAA Waiver
                                </button>
                            </h2>
                            <div id="hipaaCollapse" className="accordion-collapse collapse show" aria-labelledby="hipaaHeading" data-bs-parent="#waiverAccordion">
                                <div className="accordion-body">
                                    <p>Volunteers have the obligation to treat all information ...</p>
                                    {/* ...rest of HIPAA waiver text... */}
                                    {data.hipaa ? (
                                        <div>
                                            Date Signed: <strong>{data.hipaa.dateSigned ? new Date(data.hipaa.dateSigned).toLocaleDateString() : 'N/A'}</strong>
                                            {data.hipaa.signature && <div>Signature:<br /><img src={data.hipaa.signature} alt="HIPAA Signature" style={{ border: '1px solid #ccc', width: '100%' }} /></div>}
                                        </div>
                                    ) : <div className="text-danger">No HIPAA waiver on file.</div>}
                                </div>
                            </div>
                        </div>
                        <div className="accordion-item">
                            <h2 className="accordion-header" id="liabilityHeading">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#liabilityCollapse" aria-expanded="false" aria-controls="liabilityCollapse">
                                    Liability Waiver
                                </button>
                            </h2>
                            <div id="liabilityCollapse" className="accordion-collapse collapse" aria-labelledby="liabilityHeading" data-bs-parent="#waiverAccordion">
                                <div className="accordion-body">
                                    <p><strong>IN CONSIDERATION OF</strong> the risk of injury ...</p>
                                    {/* ...rest of Liability waiver text... */}
                                    {data.liability ? (
                                        <div>
                                            Date Signed: <strong>{data.liability.dateSigned ? new Date(data.liability.dateSigned).toLocaleDateString() : 'N/A'}</strong>
                                            {data.liability.signature && <div>Signature:<br /><img src={data.liability.signature} alt="Liability Signature" style={{ border: '1px solid #ccc', width: '100%' }} /></div>}
                                        </div>
                                    ) : <div className="text-danger">No Liability waiver on file.</div>}
                                </div>
                            </div>
                        </div>
                        {/* Add Professional and Chaplain forms if present */}
                        {data.isProfessional && data.professionalForm && (
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="professionalHeading">
                                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#professionalCollapse" aria-expanded="false" aria-controls="professionalCollapse">
                                        Professional Volunteer Form
                                    </button>
                                </h2>
                                <div id="professionalCollapse" className="accordion-collapse collapse" aria-labelledby="professionalHeading" data-bs-parent="#waiverAccordion">
                                    <div className="accordion-body">
                                        <div>Profession: <strong>{data.professionalForm.profession || ''}</strong></div>
                                        {/* ...other fields... */}
                                    </div>
                                </div>
                            </div>
                        )}
                        {data.isChaplin && data.chaplinDetails && (
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="chaplainHeading">
                                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#chaplainCollapse" aria-expanded="false" aria-controls="chaplainCollapse">
                                        Chaplain Details
                                    </button>
                                </h2>
                                <div id="chaplainCollapse" className="accordion-collapse collapse" aria-labelledby="chaplainHeading" data-bs-parent="#waiverAccordion">
                                    <div className="accordion-body">
                                        <div>Formally Credentialed: <strong>{data.chaplinDetails.formallyCredentialed ? 'Yes' : 'No'}</strong></div>
                                        <div>Reason Serving: <strong>{data.chaplinDetails.reasonServing || ''}</strong></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
                setFormsContent(html);
            })
            .catch(() => setFormsContent(<div className="text-danger">Failed to load waiver forms.</div>));
    };

    // Logout handler
    const handleLogout = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You will be logged out!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, log out!'
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(baseAPIurl + '/api/foodPantry/logoutUser', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                })
                    .then(res => res.json())
                    .then(data => {
                        if (data.status === 'success') {
                            Swal.fire('Logged out!', 'You have been logged out.', 'success').then(() => {
                                window.location.href = 'userLogin.html';
                            });
                        } else {
                            Swal.fire('Error!', 'There was an error logging you out.', 'error');
                        }
                    })
                    .catch(() => Swal.fire('Error!', 'There was an error logging you out.', 'error'));
            }
        });
    };

    // Pie chart data
    const pieData = {
        labels: volunteerTypes.labels,
        datasets: [
            {
                data: volunteerTypes.data,
                backgroundColor: ['#007bff', '#28a745', '#17a2b8'],
            },
        ],
    };

    return (
        <div className="container-fluid p-0" >


            {/* Dashboard Cards */}
            <div className="container-fluid content-inner mt-1">
                <div className="row mb-4">
                    <div className="col-12">
                        <h3 className="fw-bold text-dark mb-3">General Dashboard</h3>
                    </div>
                </div>
                <div className="row mb-4">
                    <div className="col-md-4 mb-3">
                        <div className="card shadow-lg rounded-4 h-100">
                            <div className="card-body d-flex flex-column align-items-center justify-content-center">
                                <h5 className="card-title text-center">Volunteers Registered</h5>
                                <h2 className="fw-bold text-primary">{dashboardStats.volunteersRegistered}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className="card shadow-lg rounded-4 h-100">
                            <div className="card-body d-flex flex-column align-items-center justify-content-center">
                                <h5 className="card-title text-center">Patients Served</h5>
                                <h2 className="fw-bold text-success">{dashboardStats.patientsServed}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className="card shadow-lg rounded-4 h-100">
                            <div className="card-body d-flex flex-column align-items-center justify-content-center">
                                <h5 className="card-title text-center">Clinics Hosted</h5>
                                <h2 className="fw-bold text-info">{dashboardStats.clinicsHosted}</h2>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-6 mb-4">
                        <div className="card shadow-lg rounded-4 h-100">
                            <div className="card-header bg-white">
                                <h5 className="mb-0">Upcoming Deployments</h5>
                            </div>
                            <div className="card-body">
                                <ul className="list-group list-group-flush">
                                    {upcomingDeployments.length === 0 ? (
                                        <li className="list-group-item text-muted">No upcoming deployments.</li>
                                    ) : (
                                        upcomingDeployments.map((dep, idx) => (
                                            <li className="list-group-item" key={idx}>{dep}</li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 mb-4">
                        <div className="card shadow-lg rounded-4 h-100">
                            <div className="card-header bg-white">
                                <h5 className="mb-0">Volunteer Types</h5>
                            </div>
                            <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 250 }}>
                                <Pie data={pieData} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Forms Modal */}
            <Modal show={showFormsModal} onShow={loadFormsContent} onHide={() => setShowFormsModal(false)} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>My Forms</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formsContent}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="warning" className="text-dark fw-bold rounded-2">Download All</Button>
                    <Button variant="dark" className="fw-bold rounded-2" onClick={() => setShowFormsModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Settings Modal */}
            <Modal show={showSettingsModal} onHide={() => setShowSettingsModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>User Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="SettingsFirstName">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control type="text" value={settings.firstName} onChange={e => setSettings({ ...settings, firstName: e.target.value })} required />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="SettingsLastName">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control type="text" value={settings.lastName} onChange={e => setSettings({ ...settings, lastName: e.target.value })} required />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="SettingsEmail">
                            <Form.Label>Email</Form.Label>
                            <Form.Control type="email" value={settings.email} onChange={e => setSettings({ ...settings, email: e.target.value })} required />
                        </Form.Group>
                        <Button variant="outline-danger" className="mb-3" onClick={() => setShowPasswordFields(!showPasswordFields)}>
                            Change Password
                        </Button>
                        {showPasswordFields && (
                            <>
                                <Form.Group className="mb-3" controlId="SettingsPassword">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control type="password" required />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="SettingsCnfrmPassword">
                                    <Form.Label>Confirm Password</Form.Label>
                                    <Form.Control type="password" required />
                                </Form.Group>
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
                    <Button variant="success">Save Changes</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GeneralDashboard;
